// LessonFeedbackCard.tsx — the end-of-CHAPTER "How was this?" micro-survey.
//
// Asked at most once per chapter, always skippable. Sentiment is one tap; the optional
// note is free-text that is stored ANONYMOUSLY on the server (no user link) — so the
// UI explicitly asks the user not to include names or personal details.
import React from 'react';
import { Btn, Body } from '@/components/ui';
import { Icon, type IconName } from '@/components/icons';

export type Sentiment = 'learning' | 'confusing' | 'unsettling';

// NOTE: the stored ids ('learning' | 'confusing' | 'unsettling') are kept stable so
// the research dataset stays continuous; only the on-screen labels are tuned here for
// a warmer, content-fault (never user-fault), trauma-safe read.
const OPTIONS: { id: Sentiment; label: string; icon: IconName; tint: string }[] = [
  { id: 'learning', label: 'This helped', icon: 'sparkle', tint: '29,158,117' },
  { id: 'confusing', label: 'A bit confusing', icon: 'compass', tint: '201,153,42' },
  { id: 'unsettling', label: 'Hard to sit with', icon: 'heart', tint: '196,108,108' },
];

export function LessonFeedbackCard({
  onSubmit,
  onSkip,
}: {
  /** Send the feedback. note is optional anonymous free-text. */
  onSubmit: (sentiment: Sentiment, note?: string) => void;
  /** Dismiss without answering (still counts as "asked", so it won't reappear). */
  onSkip: () => void;
}) {
  const [picked, setPicked] = React.useState<Sentiment | null>(null);
  const [note, setNote] = React.useState('');

  return (
    <div style={scrim}>
      <div style={card}>
        <Body size={16.5} style={{ fontFamily: 'Fraunces, Georgia, serif', color: 'var(--ink)' }}>
          How was this chapter?
        </Body>
        <Body size={12.5} style={{ marginTop: 4, color: 'var(--ink-soft)' }}>
          Optional — it helps us improve the programme.
        </Body>

        <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
          {OPTIONS.map((o) => {
            const on = picked === o.id;
            return (
              <button
                key={o.id}
                onClick={() => setPicked(o.id)}
                style={{
                  flex: 1,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: 7,
                  padding: '13px 6px',
                  borderRadius: 15,
                  cursor: 'pointer',
                  background: on ? `rgba(${o.tint},0.13)` : 'var(--paper)',
                  border: `1.5px solid ${on ? `rgba(${o.tint},0.85)` : 'var(--hair)'}`,
                  color: on ? `rgb(${o.tint})` : 'var(--ink-soft)',
                  transition: 'all .15s',
                }}
              >
                <Icon name={o.icon} size={20} />
                <span style={{ fontSize: 12.5, fontWeight: 600, textAlign: 'center', lineHeight: 1.2 }}>{o.label}</span>
              </button>
            );
          })}
        </div>

        {picked && (
          <div style={{ marginTop: 14, animation: 'rise .25s ease' }}>
            <textarea
              value={note}
              onChange={(e) => setNote(e.target.value.slice(0, 600))}
              rows={3}
              placeholder="Anything you’d like to add? (optional)"
              style={{
                width: '100%',
                resize: 'none',
                borderRadius: 13,
                border: '1px solid var(--hair-strong)',
                background: 'var(--paper)',
                padding: '11px 13px',
                fontSize: 14,
                fontFamily: 'inherit',
                color: 'var(--ink)',
                lineHeight: 1.5,
                boxSizing: 'border-box',
              }}
            />
            <Body size={11.5} style={{ marginTop: 7, color: 'var(--ink-soft)', display: 'flex', gap: 6, lineHeight: 1.45 }}>
              <span style={{ flexShrink: 0, marginTop: 1, color: 'var(--green-deep)' }}>
                <Icon name="lock" size={12} />
              </span>
              <span>
                This note is anonymous and only used to improve the lesson. Please don’t include names or
                personal details.
              </span>
            </Body>
          </div>
        )}

        <div style={{ marginTop: 18, display: 'flex', gap: 10 }}>
          <Btn variant="ghost" size="sm" onClick={onSkip} style={{ flex: '0 0 auto' }}>
            Skip
          </Btn>
          <Btn
            size="sm"
            onClick={() => picked && onSubmit(picked, note.trim() || undefined)}
            disabled={!picked}
            style={{ flex: 1, opacity: picked ? 1 : 0.5 }}
          >
            Send
          </Btn>
        </div>
      </div>
    </div>
  );
}

const scrim: React.CSSProperties = {
  position: 'fixed',
  inset: 0,
  zIndex: 110,
  background: 'rgba(20,40,33,0.4)',
  backdropFilter: 'blur(3px)',
  WebkitBackdropFilter: 'blur(3px)',
  display: 'flex',
  alignItems: 'flex-end',
  justifyContent: 'center',
  padding: 'calc(var(--safe-top) + 18px) 16px calc(var(--safe-bottom) + 18px)',
};
const card: React.CSSProperties = {
  width: '100%',
  maxWidth: 460,
  margin: 'auto 0 0',
  background: 'var(--cream)',
  borderRadius: 24,
  padding: '22px 22px calc(20px + var(--safe-bottom))',
  boxShadow: '0 -8px 40px -12px rgba(20,60,45,0.45)',
  animation: 'rise .4s cubic-bezier(.2,.7,.3,1)',
};
